import { useState, useEffect } from 'react';
import MonacoEditor from '@monaco-editor/react';
import ReactMarkdown from 'react-markdown';

export interface TextEditorProps {
  content: string;
  label?: string;
  onSave: (content: string) => Promise<void>;
}

export default function TextEditor({ content, label, onSave }: TextEditorProps) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(content || '');
  const [loading, setLoading] = useState(false);

  // Update the value when content changes from props
  useEffect(() => {
    setValue(content || '');
  }, [content]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await onSave(value);
      setEditing(false);
    } catch (error) {
      console.error('Error saving content:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="text-editor mb-4">
      {!editing ? (
        <div className="relative group">
          <div className="markdown-preview prose max-w-none">
            {value ? (
              <ReactMarkdown>{value}</ReactMarkdown>
            ) : (
              <p className="text-gray-500 italic">No content available. Click Edit to add content.</p>
            )}
          </div>
          <button 
            onClick={() => setEditing(true)} 
            className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 text-blue-500 hover:text-blue-700 focus:outline-none bg-white bg-opacity-75 rounded-md p-1"
            title="Edit this content"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          {label && (
            <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
              <h3 className="text-sm font-medium text-gray-700">{label}</h3>
            </div>
          )}
          <div className="p-4">
            <MonacoEditor
              height="150px"
              defaultLanguage="markdown"
              value={value}
              onChange={(val) => setValue(val ?? '')}
              options={{
                minimap: { enabled: false },
                wordWrap: 'on',
                fontSize: 14,
                scrollBeyondLastLine: false,
                lineNumbers: 'on',
                scrollbar: {
                  vertical: 'visible',
                  horizontal: 'visible',
                },
              }}
            />
            <div className="markdown-preview border-t mt-4 pt-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Preview:</h4>
              <div className="prose max-w-none border-l-4 border-blue-100 pl-4">
                <ReactMarkdown>{value}</ReactMarkdown>
              </div>
            </div>
            <div className="flex justify-end mt-4 space-x-2">
              <button 
                className="inline-flex items-center bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors" 
                onClick={handleSave} 
                disabled={loading}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Save Changes
                  </>
                )}
              </button>
              <button 
                className="inline-flex items-center bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md text-sm font-medium transition-colors" 
                onClick={() => {
                  setValue(content);
                  setEditing(false);
                }}
                disabled={loading}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
